import { rgbArrayToHex } from "./utils.js";
import { GeoJSON as olFormatGeoJSON } from 'ol/format.js';
import { Circle as olStyleCircle, Icon as olStyleIcon, Stroke } from 'ol/style.js';
import Feature from 'ol/Feature.js';
import { getUid } from 'ol';
import { asArray } from 'ol/color.js';
import { toDegrees } from 'ol/math.js';
import VectorSource from 'ol/source/Vector.js';
import { fromCircle } from 'ol/geom/Polygon.js';
import { Constants } from "./constants.js";
import { getFontParameters } from 'ol/css.js';
/** Represents the different types of printing styles. */
export const PrintStyleType = {
    LINE_STRING: 'LineString',
    POINT: 'Point',
    POLYGON: 'Polygon',
};
/**
 * Link between supported geometry and print style types.
 * Circles will be handled as polygon.
 * */
export const PrintStyleTypes = Object.freeze({
    LineString: PrintStyleType.LINE_STRING,
    Point: PrintStyleType.POINT,
    Polygon: PrintStyleType.POLYGON,
    MultiLineString: PrintStyleType.LINE_STRING,
    MultiPoint: PrintStyleType.POINT,
    MultiPolygon: PrintStyleType.POLYGON,
});
function isSupportedGeometryType(geometryType) {
    return geometryType in PrintStyleTypes;
}
/** Key prefix to feature style prop */
const FEATURE_STYLE_PROP = '_mfp_style';
/**
 * Calculates the priority of a GeoJSON feature based on its feature type.
 * For sort functions, to let points appearing to the top.
 * @returns The priority value.
 */
const featureTypePriority_ = (feature) => {
    const geometry = feature.geometry;
    if (geometry && geometry.type === 'Point') {
        return 0;
    }
    else {
        return 1;
    }
};
/**
 * @returns A string or an array of strings into a formatted style key.
 */
const styleKey = (styles) => {
    const keys = Array.isArray(styles) ? styles.join(',') : styles;
    return `[${FEATURE_STYLE_PROP} = '${keys}']`;
};
/**
 * Convert a given OpenLayers layer to the MapFishPrint v3 format.
 * The conversion can be customized by:
 * - extending the class;
 * - passing a customizer.
 */
export default class VectorEncoder {
    layerState_;
    // VectorSource<Feature> for OL 10.0.0, any to support OL < 10
    layer_;
    customizer_;
    geojsonFormat = new olFormatGeoJSON();
    deepIds_ = new Map();
    lastDeepId_ = 0;
    constructor(layerState, customizer) {
        this.layerState_ = layerState;
        // VectorSource<Feature> for OL 10.0.0, any to support OL < 10
        this.layer_ = this.layerState_.layer;
        this.customizer_ = customizer;
    }
    /**
     * Encodes the vector layer into a mapfish vector layer object.
     * @returns The encoded vector layer object or null if the layer is empty.
     */
    encodeVectorLayer(resolution) {
        const source = this.layer_.getSource();
        if (!source) {
            return null; // skipping
        }
        console.assert(source instanceof VectorSource);
        const features = source.getFeaturesInExtent(this.customizer_.getPrintExtent());
        const geojsonFeatures = [];
        const mapfishStyleObject = {
            version: 2,
        };
        features.forEach((feature) => this.encodeFeature(feature, resolution, geojsonFeatures, mapfishStyleObject));
        // MapFish Print fails if there are no style rules, even if there are no
        // features either. To work around this we just ignore the layer if the
        // array of GeoJSON features is empty.
        // See https://github.com/mapfish/mapfish-print/issues/279
        if (geojsonFeatures.length <= 0) {
            return null;
        }
        // And if there are no properties except the version in the style, ignore the layer.
        if (Object.keys(mapfishStyleObject).length <= 1) {
            return null;
        }
        // Reorder features: put points last, such that they appear on top
        geojsonFeatures.sort((feature0, feature1) => {
            const priority = featureTypePriority_;
            return priority(feature1) - priority(feature0);
        });
        const geojsonFeatureCollection = {
            type: 'FeatureCollection',
            features: geojsonFeatures,
        };
        return {
            geoJson: geojsonFeatureCollection,
            opacity: this.layerState_.opacity,
            style: mapfishStyleObject,
            type: 'geojson',
            name: this.layer_.get('name'),
        };
    }
    /**
     * Encodes a feature into a GeoJSON feature based and adds it to the array of GeoJSON features.
     * Complete the mapfishStyleObject with the related styles.
     */
    encodeFeature(feature, resolution, geojsonFeatures, mapfishStyleObject) {
        let styleData = null;
        const styleFunction = feature.getStyleFunction() || this.layer_.getStyleFunction();
        if (styleFunction) {
            styleData = styleFunction(feature, resolution);
        }
        if (feature.getGeometry().getType() === 'Circle') {
            feature = this.featureCircleAsPolygon(feature);
        }
        const origGeojsonFeature = this.geojsonFormat.writeFeatureObject(feature);
        let styles = styleData !== null && !Array.isArray(styleData) ? [styleData] : styleData;
        if (!styles) {
            return;
        }
        styles = styles.filter((style) => !!style);
        if (styles.length === 0) {
            return;
        }
        console.assert(Array.isArray(styles));
        let isOriginalFeatureAdded = false;
        styles.forEach((style) => {
            // FIXME: the return of the function is very complicate and would require
            // handling more cases than we actually do
            let geometry = style.getGeometry();
            let geojsonFeature;
            if (geometry) {
                const styledFeature = feature.clone();
                styledFeature.setGeometry(geometry);
                geojsonFeature = this.geojsonFormat.writeFeatureObject(styledFeature);
                geojsonFeatures.push(geojsonFeature);
            }
            else {
                geojsonFeature = origGeojsonFeature;
                geometry = feature.getGeometry();
                // no need to encode features with no geometry
                if (!geometry) {
                    return;
                }
                if (!this.customizer_.geometryFilter(geometry)) {
                    return;
                }
                if (!isOriginalFeatureAdded) {
                    geojsonFeatures.push(geojsonFeature);
                    isOriginalFeatureAdded = true;
                }
            }
            this.addVectorStyle(mapfishStyleObject, geojsonFeature, style);
        });
    }
    /**
     * @returns The unique identifier for the given style.
     */
    getDeepStyleUid(style) {
        const todo = [style];
        let key = '';
        while (todo.length) {
            const obj = todo.pop();
            key += '_k' + getUid(obj);
            for (const [k, value] of Object.entries(obj)) {
                if (value !== null && value !== undefined) {
                    if (['number', 'string', 'boolean'].includes(typeof value)) {
                        key += `_${k}:${value}`;
                    }
                    else {
                        todo.push(value);
                    }
                }
            }
        }
        if (this.deepIds_.has(key)) {
            return this.deepIds_.get(key).toString();
        }
        const uid = ++this.lastDeepId_;
        this.deepIds_.set(key, uid);
        return uid.toString();
    }
    /**
     * Adds a vector style to the mapfishStyleObject based on the given parameters.
     */
    addVectorStyle(mapfishStyleObject, geojsonFeature, style) {
        const styleId = this.getDeepStyleUid(style);
        const key = styleKey(styleId);
        let hasSymbolizer;
        if (key in mapfishStyleObject) {
            // do nothing if we already have a style object for this CQL rule
            hasSymbolizer = true;
        }
        else {
            const styleObject = this.encodeVectorStyle(geojsonFeature, style);
            hasSymbolizer = styleObject && styleObject.symbolizers.length !== 0;
            if (hasSymbolizer) {
                // @ts-ignore
                mapfishStyleObject[key] = styleObject;
            }
        }
        if (hasSymbolizer) {
            if (!geojsonFeature.properties) {
                geojsonFeature.properties = {};
            }
            this.customizer_.feature(this.layerState_, geojsonFeature);
            const existingStylesIds = geojsonFeature.properties[FEATURE_STYLE_PROP];
            if (existingStylesIds) {
                // multiple styles: merge symbolizers
                const styleIds = [...existingStylesIds.split(','), styleId];
                // @ts-ignore
                mapfishStyleObject[styleKey(styleIds)] = {
                    symbolizers: [
                        // @ts-ignore
                        ...mapfishStyleObject[styleKey(existingStylesIds)].symbolizers,
                        // @ts-ignore
                        ...mapfishStyleObject[key].symbolizers,
                    ],
                };
                geojsonFeature.properties[FEATURE_STYLE_PROP] = styleIds.join(',');
            }
            else {
                geojsonFeature.properties[FEATURE_STYLE_PROP] = styleId;
            }
        }
    }
    /**
     * Encodes the vector style based on the geometry type and style.
     * @returns The encoded vector style, or null if the geometry type is unsupported.
     */
    encodeVectorStyle(geojsonFeature, style) {
        const geometryType = geojsonFeature.geometry.type;
        if (!isSupportedGeometryType(geometryType)) {
            console.warn('Unsupported geometry type: ', geometryType);
            return null;
        }
        const styleType = PrintStyleTypes[geometryType];
        const styleObject = {
            symbolizers: [],
        };
        const fillStyle = style.getFill();
        const imageStyle = style.getImage();
        const strokeStyle = style.getStroke();
        const textStyle = style.getText();
        if (styleType === PrintStyleType.POLYGON) {
            if (fillStyle !== null) {
                this.encodeVectorStylePolygon(geojsonFeature, styleObject.symbolizers, fillStyle, strokeStyle);
            }
        }
        else if (styleType === PrintStyleType.LINE_STRING) {
            if (strokeStyle !== null) {
                this.encodeVectorStyleLine(geojsonFeature, styleObject.symbolizers, strokeStyle);
            }
        }
        else if (styleType === PrintStyleType.POINT) {
            if (imageStyle !== null) {
                this.encodeVectorStylePoint(geojsonFeature, styleObject.symbolizers, imageStyle);
            }
        }
        if (textStyle !== null) {
            this.encodeVectorStyleText(geojsonFeature, styleObject.symbolizers, textStyle);
        }
        return styleObject;
    }
    /**
     * Encodes the vector style fill for a symbolizer.
     */
    encodeVectorStyleFill(geojsonFeature, symbolizer, fillStyle) {
        let fillColor = fillStyle.getColor();
        if (fillColor === null) {
            return;
        }
        console.assert(typeof fillColor === 'string' || Array.isArray(fillColor));
        // @ts-ignore
        fillColor = asArray(fillColor);
        console.assert(Array.isArray(fillColor), 'only supporting fill colors');
        symbolizer.fillColor = rgbArrayToHex(fillColor);
        symbolizer.fillOpacity = fillColor[3];
    }
    /**
     * Encodes the vector style for a line symbolizer, using the given stroke style.
     */
    encodeVectorStyleLine(geojsonFeature, symbolizers, strokeStyle) {
        const symbolizer = {
            type: 'line',
        };
        this.encodeVectorStyleStroke(geojsonFeature, symbolizer, strokeStyle);
        this.customizer_.line(this.layerState_, geojsonFeature, symbolizer, strokeStyle);
        symbolizers.push(symbolizer);
    }
    /**
     * Encodes a vector style point.
     */
    encodeVectorStylePoint(geojsonFeature, symbolizers, imageStyle) {
        let symbolizer;
        if (imageStyle instanceof olStyleCircle) {
            symbolizer = this.encodeVectorStylePointStyleCircle(geojsonFeature, imageStyle);
        }
        else if (imageStyle instanceof olStyleIcon) {
            symbolizer = this.encodeVectorStylePointStyleIcon(geojsonFeature, imageStyle);
        }
        if (symbolizer) {
            this.customizer_.point(this.layerState_, geojsonFeature, symbolizer, imageStyle);
            symbolizers.push(symbolizer);
        }
    }
    /**
     * Encodes the vector style point style circle.
     * @returns The encoded symbolizer point.
     */
    encodeVectorStylePointStyleCircle(geojsonFeature, imageStyle) {
        const symbolizer = {
            type: 'point',
        };
        symbolizer.pointRadius = imageStyle.getRadius();
        const scale = imageStyle.getScale();
        if (scale) {
            if (Array.isArray(scale)) {
                symbolizer.pointRadius *= (scale[0] + scale[1]) / 2;
            }
            else {
                symbolizer.pointRadius *= scale;
            }
        }
        const fillStyle = imageStyle.getFill();
        let strokeStyle = imageStyle.getStroke();
        if (fillStyle !== null) {
            this.encodeVectorStyleFill(geojsonFeature, symbolizer, fillStyle);
            if (!strokeStyle) {
                // Without a stroke style a black border would appear around the circle
                strokeStyle = new Stroke({
                    color: fillStyle.getColor(),
                });
            }
        }
        if (strokeStyle !== null) {
            this.encodeVectorStyleStroke(geojsonFeature, symbolizer, strokeStyle);
        }
        return symbolizer;
    }
    /**
     * Encodes a Vector Style point style icon.
     * @returns The encoded symbolizer point style or undefined if imageStyle src is undefined.
     */
    encodeVectorStylePointStyleIcon(geojsonFeature, imageStyle) {
        const src = imageStyle.getSrc();
        if (src === undefined) {
            return undefined;
        }
        const symbolizer = {
            type: 'point',
            externalGraphic: src,
        };
        const opacity = imageStyle.getOpacity();
        if (opacity !== null) {
            symbolizer.graphicOpacity = opacity;
        }
        const size = imageStyle.getSize();
        if (size !== null) {
            let scale = imageStyle.getScale();
            if (Array.isArray(scale)) {
                scale = (scale[0] + scale[1]) / 2;
            }
            if (isNaN(scale)) {
                scale = 1;
            }
            const width = size[0] * scale;
            const height = size[1] * scale;
            // Note: 'graphicWidth' is misnamed as of mapfish-console.log 3.14.1, it actually sets the height
            symbolizer.graphicWidth = height;
            this.addGraphicOffset_(symbolizer, imageStyle, width, height);
        }
        let rotation = imageStyle.getRotation();
        if (isNaN(rotation)) {
            rotation = 0;
        }
        symbolizer.rotation = toDegrees(rotation);
        return symbolizer;
    }
    /**
     * Add the graphic offset to the symbolizer.
     */
    addGraphicOffset_(symbolizer, icon, width, height) {
        if (this.hasDefaultAnchor_(icon)) {
            return;
        }
        const topLeftOffset = icon.getAnchor();
        const centerXOffset = width / 2 - topLeftOffset[0];
        const centerYOffset = height / 2 - topLeftOffset[1];
        symbolizer.graphicXOffset = centerXOffset;
        symbolizer.graphicYOffset = centerYOffset;
    }
    /**
     * Checks if the provided icon has default anchor properties.
     * @returns true if the icon has default anchor properties, otherwise false.
     */
    hasDefaultAnchor_(icon) {
        // @ts-ignore
        const icon_ = icon;
        const hasDefaultCoordinates = icon_.anchor_[0] === 0.5 && icon_.anchor_[1] === 0.5;
        const hasDefaultOrigin = icon_.anchorOrigin_ === 'top-left';
        const hasDefaultXUnits = icon_.anchorXUnits_ === 'fraction';
        const hasDefaultYUnits = icon_.anchorYUnits_ === 'fraction';
        return hasDefaultCoordinates && hasDefaultOrigin && hasDefaultXUnits && hasDefaultYUnits;
    }
    /**
     * Encodes the vector style of a polygon by applying fill and stroke styles.
     */
    encodeVectorStylePolygon(geojsonFeature, symbolizers, fillStyle, strokeStyle) {
        const symbolizer = {
            type: 'polygon',
        };
        this.encodeVectorStyleFill(geojsonFeature, symbolizer, fillStyle);
        if (strokeStyle !== null) {
            this.encodeVectorStyleStroke(geojsonFeature, symbolizer, strokeStyle);
        }
        symbolizers.push(symbolizer);
    }
    /**
     * Encodes the vector style stroke properties.
     */
    encodeVectorStyleStroke(geojsonFeature, symbolizer, strokeStyle) {
        const strokeColor = strokeStyle.getColor();
        if (strokeColor !== null) {
            console.assert(typeof strokeColor === 'string' || Array.isArray(strokeColor));
            // @ts-ignore
            const strokeColorRgba = asArray(strokeColor);
            console.assert(Array.isArray(strokeColorRgba), 'only supporting stroke colors');
            symbolizer.strokeColor = rgbArrayToHex(strokeColorRgba);
            symbolizer.strokeOpacity = strokeColorRgba[3];
        }
        const strokeDashstyle = strokeStyle.getLineDash();
        if (strokeDashstyle !== null) {
            symbolizer.strokeDashstyle = strokeDashstyle.join(' ');
        }
        const strokeWidth = strokeStyle.getWidth();
        if (strokeWidth !== undefined) {
            symbolizer.strokeWidth = strokeWidth;
        }
        const strokeLineCap = strokeStyle.getLineCap();
        if (strokeLineCap) {
            symbolizer.strokeLinecap = strokeLineCap;
        }
        const strokeLineJoin = strokeStyle.getLineJoin();
        if (strokeLineJoin) {
            symbolizer.strokeLinejoin = strokeLineJoin;
        }
    }
    /**
     * Encodes vector style text.
     */
    encodeVectorStyleText(geojsonFeature, symbolizers, textStyle) {
        const label = textStyle.getText();
        if (label) {
            const fp = getFontParameters(textStyle.getFont() || 'sans-serif');
            const symbolizer = {
                type: 'text',
                label: textStyle.getText(),
                fontFamily: fp.family,
                fontSize: fp.size,
                fontStyle: fp.style,
                fontWeight: fp.weight,
                // FIXME: missing fontVariant, is it supported in MFP?
                labelXOffset: textStyle.getOffsetX(),
                // OL and MFP behaves differently on the Y offset
                labelYOffset: -textStyle.getOffsetY(),
                labelAlign: 'cm',
            };
            const fillStyle = textStyle.getFill();
            if (fillStyle !== null) {
                this.encodeVectorStyleFill(geojsonFeature, symbolizer, fillStyle);
                symbolizer.fontColor = symbolizer.fillColor;
            }
            const strokeStyle = textStyle.getStroke();
            if (strokeStyle !== null) {
                const strokeColor = strokeStyle.getColor();
                if (strokeColor) {
                    console.assert(typeof strokeColor === 'string' || Array.isArray(strokeColor));
                    // @ts-ignore
                    const strokeColorRgba = asArray(strokeColor);
                    console.assert(Array.isArray(strokeColorRgba), 'only supporting stroke colors');
                    symbolizer.haloColor = rgbArrayToHex(strokeColorRgba);
                    symbolizer.haloOpacity = strokeColorRgba[3];
                }
                const strokeWidth = strokeStyle.getWidth();
                if (strokeWidth !== undefined) {
                    symbolizer.haloRadius = strokeWidth;
                }
            }
            this.customizer_.text(this.layerState_, geojsonFeature, symbolizer, textStyle);
            symbolizers.push(symbolizer);
        }
    }
    /**
     * Converts a circle feature to a N sides polygon feature.
     * Sides are defined in Constants.CIRCLE_TO_POLYGON_SIDES.
     */
    featureCircleAsPolygon(feature) {
        return new Feature({
            ...feature.getProperties(),
            geometry: fromCircle(feature.getGeometry(), Constants.CIRCLE_TO_POLYGON_SIDES),
        });
    }
}
//# sourceMappingURL=data:application/json;base64,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